import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { products } from '../utils/productsData';
import SEO from '../components/SEO';
import { X, CheckCircle2 } from 'lucide-react';
import '../styles/Products.css';

const Products = () => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('Camera Combo');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const { addToCart } = useCart();
  const [showNotification, setShowNotification] = useState(false);
  const [notificationProduct, setNotificationProduct] = useState(null);
  // const [showLikeErrorNotification, setShowLikeErrorNotification] = useState(false);

  // Handle body overflow when modal is open
  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showModal]);

  // Map search terms to category names
  const searchToCategoryMap = useMemo(() => ({
    'lens': 'Lens',
    'lenses': 'Lens',
    'camera': 'Camera',
    'cameras': 'Camera',
    'combo': 'Camera Combo',
    'combination': 'Camera Combo',
    'bundle': 'Camera Combo',
    'camera combo': 'Camera Combo',
    'light': 'Lights',
    'lights': 'Lights',
    'lighting': 'Lights',
    'gimbal': 'Gimbal',
    'gimbals': 'Gimbal',
    'audio': 'Audio',
    'microphone': 'Audio',
    'mic': 'Audio',
    'studio': 'Studio',
    'tripod': 'Tripod',
    'tripods': 'Tripod',
    'filter': 'Filter',
    'filters': 'Filter',
    'uv': 'Filter',
    'polarizing': 'Filter',
    'nd': 'Filter',
    'graduated': 'Filter',
    'accessories': 'Accessories',
    'monitor': 'Monitor'
  }), []);

  // Handle URL parameters (search, category, productId)
  useEffect(() => {
    const searchParam = searchParams.get('search');
    const categoryParam = searchParams.get('category');
    const productIdParam = searchParams.get('productId');
    
    // Handle category parameter
    if (categoryParam) {
      setCategoryFilter(categoryParam);
      setSearchQuery(''); // Clear search when category is set
    }
    
    // Handle search parameter
    if (searchParam) {
      const searchLower = searchParam.toLowerCase();
      setSearchQuery(searchParam);
      
      // Map search term to category and set the filter if not already set
      const mappedCategory = searchToCategoryMap[searchLower];
      if (mappedCategory && !categoryParam) {
        setCategoryFilter(mappedCategory);
      }
    }
    
    // Handle productId parameter - open product modal
    if (productIdParam) {
      const productId = parseInt(productIdParam);
      const product = products.find(p => p.id === productId);
      if (product) {
        // Set category filter to the product's category
        setCategoryFilter(product.category);
        setSearchQuery('');
        
        // Open the product modal
        setSelectedProduct(product);
        setShowModal(true);
      }
    }
  }, [searchParams, searchToCategoryMap]);

  // Filter products
  const filteredProducts = products.filter(product => {
    const searchLower = searchQuery.trim().toLowerCase();
    const hasSearchQuery = searchLower.length > 0;
    
    // Handle header products - only show if they have products in their category
    if (product.type === 'header') {
      if (hasSearchQuery) {
        // When searching, only show headers if there are matching products in that category
        // Use the same search logic: prioritize product name matches with brand awareness
        const brandNames = ['sony', 'canon', 'nikon', 'panasonic', 'fujifilm', 'olympus', 'sigma', 'tamron', 'zeiss'];
        const isBrandSearch = brandNames.includes(searchLower);
        
        const categoryProducts = products.filter(p => {
          if (p.type === 'header' || !p.name) return false;
          if (p.category !== product.category) return false;
          
          const pNameLower = p.name.toLowerCase();
          let nameMatches = false;
          
          if (isBrandSearch) {
            // For brand searches, match only if brand is at start or as standalone word
            nameMatches = pNameLower.startsWith(searchLower + ' ') || 
                         pNameLower.startsWith(searchLower) ||
                         pNameLower.includes(' ' + searchLower + ' ') ||
                         pNameLower === searchLower;
          } else {
            nameMatches = pNameLower.includes(searchLower);
          }
          
          // Check category mappings
          const searchMappings = {
            'lens': ['Lens'], 'lenses': ['Lens'],
            'camera': ['Camera'], 'cameras': ['Camera'],
            'combo': ['Camera Combo'], 'combination': ['Camera Combo'],
            'bundle': ['Camera Combo'], 'camera combo': ['Camera Combo'],
            'light': ['Lights'], 'lights': ['Lights'], 'lighting': ['Lights'],
            'gimbal': ['Gimbal'], 'gimbals': ['Gimbal'],
            'audio': ['Audio'], 'microphone': ['Audio'], 'mic': ['Audio'],
            'studio': ['Studio'],
            'tripod': ['Tripod'], 'tripods': ['Tripod'],
            'filter': ['Filter'], 'filters': ['Filter'],
            'uv': ['Filter'], 'polarizing': ['Filter'], 'nd': ['Filter'], 'graduated': ['Filter']
          };
          const mappedCategories = searchMappings[searchLower] || [];
          const isMappedToCategory = mappedCategories.includes(p.category);
          
          return nameMatches ||
                 isMappedToCategory ||
                 (p.category && p.category.toLowerCase().includes(searchLower));
        });
        return categoryProducts.length > 0;
      } else {
        // When not searching, show headers for the selected category
        return product.category === categoryFilter;
      }
    }
    
    // Skip invalid products
    if (!product.name || product.type === 'header') {
      return false;
    }
    
    // Common search term mappings to categories
    const searchMappings = {
      'lens': ['Lens'],
      'lenses': ['Lens'],
      'camera': ['Camera'],
      'cameras': ['Camera'],
      'combo': ['Camera Combo'],
      'combination': ['Camera Combo'],
      'bundle': ['Camera Combo'],
      'camera combo': ['Camera Combo'],
      'light': ['Lights'],
      'lights': ['Lights'],
      'lighting': ['Lights'],
      'gimbal': ['Gimbal'],
      'gimbals': ['Gimbal'],
      'audio': ['Audio'],
      'microphone': ['Audio'],
      'mic': ['Audio'],
      'studio': ['Studio'],
      'tripod': ['Tripod'],
      'tripods': ['Tripod'],
      'filter': ['Filter'],
      'filters': ['Filter'],
      'uv': ['Filter'],
      'polarizing': ['Filter'],
      'nd': ['Filter'],
      'graduated': ['Filter']
    };
    
    // Check if search term maps to a category
    const mappedCategories = searchMappings[searchLower] || [];
    const isMappedToCategory = mappedCategories.includes(product.category);
    
    // Enhanced search with priority: name/brand first, then category mapping
    // Prioritize exact matches in product name to avoid showing irrelevant products
    const productNameLower = product.name ? product.name.toLowerCase() : '';
    
    // Check if search term is a brand name (common brands)
    const brandNames = ['sony', 'canon', 'nikon', 'panasonic', 'fujifilm', 'olympus', 'sigma', 'tamron', 'zeiss'];
    const isBrandSearch = brandNames.includes(searchLower);
    
    let nameMatches = false;
    
    if (isBrandSearch) {
      // For brand searches, only match if brand appears at the start of product name or as a standalone word
      // This prevents false matches (e.g., "Sony E" mount in Canon products)
      nameMatches = productNameLower.startsWith(searchLower + ' ') || 
                    productNameLower.startsWith(searchLower) ||
                    productNameLower.includes(' ' + searchLower + ' ') ||
                    productNameLower === searchLower;
    } else {
      // For non-brand searches, use standard substring matching
      nameMatches = productNameLower.includes(searchLower);
    }
    
    // Enhanced search: name/brand, category mapping, or category name
    const matchesSearch = hasSearchQuery && (
      nameMatches || // Product name contains search term (highest priority)
      isMappedToCategory || // Search term maps to this category
      (product.category && product.category.toLowerCase().includes(searchLower)) // Category name matches
    );
    
    // Check category match
    const matchesCategory = product.category === categoryFilter;
    
    // If there's a search query, show all matching products (ignore category filter)
    // Otherwise, show products from the selected category
    if (hasSearchQuery) {
      return matchesSearch;
    } else {
      return matchesCategory;
    }
  });

  const handleViewDetails = (product) => {
    setSelectedProduct(product);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setTimeout(() => setSelectedProduct(null), 300);
    
    // Remove productId from URL when modal is closed
    if (searchParams.get('productId')) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('productId');
      setSearchParams(newSearchParams, { replace: true });
    }
  };

  const handleAddToCart = (product, event) => {
    if (event) event.stopPropagation();
    addToCart(product);
    setNotificationProduct(product);
    setShowNotification(true);
    setTimeout(() => {
      setShowNotification(false);
      setNotificationProduct(null);
    }, 3000);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    const queryLower = searchQuery.trim().toLowerCase();
    
    // Check if query exactly matches a single product (skip headers and invalid products)
    const exactProductMatch = products.find(
      product => 
        product.type !== 'header' && 
        product.name && 
        product.name.toLowerCase && 
        product.name.toLowerCase() === queryLower
    );
    
    if (exactProductMatch) {
      // Open the product modal directly
      setCategoryFilter(exactProductMatch.category);
      setSelectedProduct(exactProductMatch);
      setShowModal(true);
      setSearchQuery('');
    } else {
      // Check if query matches a category
      const mappedCategory = searchToCategoryMap[queryLower];
      if (mappedCategory) {
        setCategoryFilter(mappedCategory);
        setSearchQuery('');
      }
      // Otherwise, keep search active for filtered results
    }
  };

  return (
    <>
      <SEO 
        title="Rental Gears - Camera Equipment Rental | BookMyCamera"
        description="Rent professional camera equipment in Bangalore. Cameras, lenses, lighting, audio equipment, gimbals, and more. Best camera rental services for photography and videography."
        keywords="camera rental bangalore, camera equipment rental, photography gear rental, videography equipment rental, camera lens rental bangalore"
        canonicalUrl="https://bookmycamera.com/products"
      />
      <div className="products-container">
      <div className="products-header">
        <h1>Our Equipment</h1>
        <p>Professional camera equipment rental for your next shoot</p>
      </div>

      <div className="products-search-section">
        <form className="products-search-bar" onSubmit={handleSearchSubmit}>
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="products-search-icon">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
          <input
            type="text"
            placeholder="Search products by name or category..."
            value={searchQuery}
            onChange={(e) => {
              const value = e.target.value;
              setSearchQuery(value);
              // Update URL search param in real-time
              const newSearchParams = new URLSearchParams(searchParams);
              if (value.trim()) {
                newSearchParams.set('search', value.trim());
              } else {
                newSearchParams.delete('search');
              }
              setSearchParams(newSearchParams, { replace: true });
            }}
            className="products-search-input"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSearchSubmit(e);
              }
            }}
          />
          {searchQuery && (
            <button 
              type="button"
              className="products-clear-search-btn" 
              onClick={() => {
                setSearchQuery('');
                const newSearchParams = new URLSearchParams(searchParams);
                newSearchParams.delete('search');
                setSearchParams(newSearchParams, { replace: true });
              }}
              title="Clear search"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          )}
        </form>
      </div>

      <div className="filter-section">
        <button 
          className={`filter-button ${categoryFilter === 'Camera Combo' ? 'active' : ''}`}
          onClick={() => {
            setCategoryFilter('Camera Combo');
            setSearchQuery('');
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.set('category', 'Camera Combo');
            newSearchParams.delete('search');
            setSearchParams(newSearchParams, { replace: true });
          }}
        >
          Camera Combo
        </button>
        <button 
          className={`filter-button ${categoryFilter === 'Camera' ? 'active' : ''}`}
          onClick={() => {
            setCategoryFilter('Camera');
            setSearchQuery('');
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.set('category', 'Camera');
            newSearchParams.delete('search');
            setSearchParams(newSearchParams, { replace: true });
          }}
        >
          Camera
        </button>
        <button 
          className={`filter-button ${categoryFilter === 'Lens' ? 'active' : ''}`}
          onClick={() => {
            setCategoryFilter('Lens');
            setSearchQuery('');
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.set('category', 'Lens');
            newSearchParams.delete('search');
            setSearchParams(newSearchParams, { replace: true });
          }}
        >
          Lens
        </button>
        <button 
          className={`filter-button ${categoryFilter === 'Lights' ? 'active' : ''}`}
          onClick={() => {
            setCategoryFilter('Lights');
            setSearchQuery('');
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.set('category', 'Lights');
            newSearchParams.delete('search');
            setSearchParams(newSearchParams, { replace: true });
          }}
        >
          Lights
        </button>
        <button 
          className={`filter-button ${categoryFilter === 'Monitor' ? 'active' : ''}`}
          onClick={() => {
            setCategoryFilter('Monitor');
            setSearchQuery('');
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.set('category', 'Monitor');
            newSearchParams.delete('search');
            setSearchParams(newSearchParams, { replace: true });
          }}
        >
          Monitor
        </button>
        <button 
          className={`filter-button ${categoryFilter === 'Gimbal' ? 'active' : ''}`}
          onClick={() => {
            setCategoryFilter('Gimbal');
            setSearchQuery('');
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.set('category', 'Gimbal');
            newSearchParams.delete('search');
            setSearchParams(newSearchParams, { replace: true });
          }}
        >
          Gimbal & Rig
        </button>
        <div className={`dropdown-wrapper ${['Studio', 'Audio', 'Accessories', 'Filter', 'Tripod'].includes(categoryFilter) ? 'has-selection' : ''}`}>
          <select
            className={`filter-dropdown ${['Studio', 'Audio', 'Accessories', 'Filter', 'Tripod'].includes(categoryFilter) ? 'has-selection' : ''}`}
            value={['Studio', 'Audio', 'Accessories', 'Filter', 'Tripod'].includes(categoryFilter) ? categoryFilter : ''}
            onChange={(e) => {
              const value = e.target.value;
              if (value) {
                setCategoryFilter(value);
                setSearchQuery('');
                const newSearchParams = new URLSearchParams(searchParams);
                newSearchParams.set('category', value);
                newSearchParams.delete('search');
                setSearchParams(newSearchParams, { replace: true });
              }
            }}
          >
            <option value="">More Accessories</option>
            <option value="Studio">Studio</option>
            <option value="Audio">Audio</option>
            <option value="Filter">Filter</option>
            <option value="Tripod">Tripod</option>
            <option value="Accessories">Accessories</option>
          </select>
        </div>
      </div>

      <div className="products-grid">
        {filteredProducts.length === 0 || filteredProducts.filter(p => p.type !== 'header').length === 0 ? (
          <div className="no-products-message">
            <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
            <h3>Product Not Available</h3>
            <p>We couldn't find any products matching your search. Try a different keyword or browse our categories.</p>
          </div>
        ) : (
          filteredProducts.map((product, index) => {
            // Render brand header if it's a header object
            if (product.type === 'header') {
              return (
                <div key={`header-${index}-${product.brand}`} className="brand-header">
                  <h2 className="brand-header-title">{product.brand}</h2>
                </div>
              );
            }
            
            return (
            <div key={product.id} className="product-card">
            <div className="product-image">
              <img 
                src={product.image} 
                alt={product.name} 
                className="product-img"
                loading="lazy"
                decoding="async"
              />
              <div className="product-actions">
                <button 
                  className="view-icon-btn" 
                  onClick={() => handleViewDetails(product)}
                  title="View Details"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                </button>
              </div>
            </div>
            <div className="product-info">
              <div className="product-category">{product.category}</div>
              <h3>{product.name}</h3>
              {(product.category === 'Camera Combo' || product.category === 'Studio') && product.ShortDescription && (
                <p className="product-short-description">
                  {product.category === 'Studio' 
                    ? product.ShortDescription.replace(/₹/g, '') 
                    : product.ShortDescription}
                </p>
              )}
              <div className="product-footer">
                <div className="price-section">
                  {product.discount > 0 && (
                    <span className="original-price">{product.category !== 'Studio' ? '₹' : ''}{product.originalPrice}</span>
                  )}
                  <span className="product-price">{product.category !== 'Studio' ? '₹' : ''}{product.price}</span>
                </div>
                <button 
                  className="add-to-cart-btn"
                  onClick={(e) => handleAddToCart(product, e)}
                >
                  Add to Cart
                </button>
              </div>
            </div>
            </div>
          );
        })
        )}
        </div>
        
      {/* Modal for Product Details */}
      {showModal && selectedProduct && (
        <div className="product-page-modal-overlay" onClick={closeModal}>
          <div className="product-page-modal-backdrop"></div>
          <div className="product-page-modal-positioner" onClick={(e) => e.stopPropagation()}>
            <div className="product-page-modal-card product-page-extra-wide product-page-custom-design">
              <button className="product-page-close-btn" onClick={closeModal}>
                <X size={24} />
              </button>
              
              <div className="product-page-product-grid">
                {/* LEFT COL: IMAGERY */}
                <div className="product-page-image-gallery">
                  <div className="product-page-main-image">
                    <div className="product-page-spec-badge">🎥 {selectedProduct.category}</div>
                    <img 
                      src={selectedProduct.image} 
                      alt={selectedProduct.name} 
                      className="product-page-hero-img"
                      loading="eager"
                      decoding="async"
                    />
                  </div>
                  
                  {selectedProduct.specifications && Object.keys(selectedProduct.specifications).length > 0 && (
                    <div className="product-page-specs-section">
                      {Object.entries(selectedProduct.specifications).map(([key, value]) => (
                        <div key={key} className="product-page-spec-card">
                          <div className="product-page-spec-label">{key}</div>
                          <div className="product-page-spec-value">{value}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* RIGHT COL: DETAILS */}
                <div className="product-page-product-info">
                  <div className="product-page-sub-header">
                    <span className="product-page-stock">In Stock</span>
                  </div>
                  <h1 className="product-page-title">{selectedProduct.name}</h1>
                  
                  <p className="product-page-description">
                    {selectedProduct.fullDescription || selectedProduct.ShortDescription || 'Professional equipment for your creative needs.'}
                  </p>
                  
                  <div className="product-page-price-container">
                    <div className="product-page-price-wrap">
                      {selectedProduct.discount > 0 && (
                        <span className="product-page-old-price">
                          {selectedProduct.category !== 'Studio' ? '₹' : ''}{selectedProduct.originalPrice}
                        </span>
                      )}
                      <span className="product-page-current-price">
                        {selectedProduct.category !== 'Studio' ? '₹' : ''}{selectedProduct.price} 
                        <span className="product-page-per-day">/ day</span>
                      </span>
                    </div>
                    {selectedProduct.discount > 0 && (
                      <div className="product-page-save-badge">
                        SAVE {selectedProduct.category !== 'Studio' ? '₹' : ''}{parseInt(selectedProduct.originalPrice) - parseInt(selectedProduct.price)}
                      </div>
                    )}
                  </div>
                  
                  <h3 className="product-page-section-heading">What's Included</h3>
                  <div className="product-page-features-list">
                    {selectedProduct.features && selectedProduct.features.map((feature, index) => (
                      <div key={index} className="product-page-feature-item">
                        <span className="product-page-check-icon">✓</span> {feature}
                      </div>
                    ))}
                  </div>
                  
                  <div className="product-page-actions">
                    <button 
                      className="product-page-btn product-page-btn-primary"
                      onClick={() => handleAddToCart(selectedProduct)}
                    >
                      Rent Now
                    </button>
                    <button 
                      className="product-page-btn product-page-btn-outline"
                      onClick={() => handleAddToCart(selectedProduct)}
                    >
                      Add to Cart
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Notification Popup */}
      {showNotification && notificationProduct && (
        <div className="product-page-notification">
          <div className="product-page-notification-content">
            <CheckCircle2 size={24} className="product-page-notification-icon" />
            <div className="product-page-notification-text">
              <span className="product-page-notification-title">Item Added to Cart!</span>
              <span className="product-page-notification-product">{notificationProduct.name}</span>
            </div>
            <button 
              className="product-page-notification-close"
              onClick={() => {
                setShowNotification(false);
                setNotificationProduct(null);
              }}
            >
              <X size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default Products;
